import axios from 'axios';

const API_BASE = import.meta.env.PROD 
  ? 'https://food-create-app.valleyintest.workers.dev/api'
  : '/api';

export const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Suppliers
export const suppliersApi = {
  getAll: (params?: Record<string, string>) => api.get('/suppliers', { params }),
  getById: (id: string) => api.get(`/suppliers/${id}`),
  create: (data: any) => api.post('/suppliers', data),
  update: (id: string, data: any) => api.put(`/suppliers/${id}`, data),
  delete: (id: string) => api.delete(`/suppliers/${id}`),
};

// Customers
export const customersApi = {
  getAll: (params?: Record<string, string>) => api.get('/customers', { params }),
  getById: (id: string) => api.get(`/customers/${id}`),
  create: (data: any) => api.post('/customers', data),
  update: (id: string, data: any) => api.put(`/customers/${id}`, data),
  delete: (id: string) => api.delete(`/customers/${id}`),
};

// Products
export const productsApi = {
  getAll: (params?: Record<string, string>) => api.get('/products', { params }),
  getById: (id: string) => api.get(`/products/${id}`),
  create: (data: any) => api.post('/products', data),
  update: (id: string, data: any) => api.put(`/products/${id}`, data),
  delete: (id: string) => api.delete(`/products/${id}`),
};

// Brands
export const brandsApi = {
  getAll: (params?: Record<string, string>) => api.get('/brands', { params }),
  getById: (id: string) => api.get(`/brands/${id}`),
  create: (data: any) => api.post('/brands', data),
  update: (id: string, data: any) => api.put(`/brands/${id}`, data),
  delete: (id: string) => api.delete(`/brands/${id}`),
};

// Cold Storages
export const coldStoragesApi = {
  getAll: (params?: Record<string, string>) => api.get('/cold-storages', { params }),
  getById: (id: string) => api.get(`/cold-storages/${id}`),
  create: (data: any) => api.post('/cold-storages', data),
  update: (id: string, data: any) => api.put(`/cold-storages/${id}`, data),
  delete: (id: string) => api.delete(`/cold-storages/${id}`),
};

// Staff
export const staffApi = {
  getAll: (params?: Record<string, string>) => api.get('/staff', { params }),
  getById: (id: string) => api.get(`/staff/${id}`),
  create: (data: any) => api.post('/staff', data),
  update: (id: string, data: any) => api.put(`/staff/${id}`, data),
  delete: (id: string) => api.delete(`/staff/${id}`),
};

// Purchases
export const purchasesApi = {
  getAll: (params?: Record<string, string>) => api.get('/purchases', { params }),
  getById: (id: string) => api.get(`/purchases/${id}`),
  create: (data: any) => api.post('/purchases', data),
  update: (id: string, data: any) => api.put(`/purchases/${id}`, data),
  delete: (id: string) => api.delete(`/purchases/${id}`),
};

// Inventory
export const inventoryApi = {
  getChilled: (params?: Record<string, string>) => api.get('/inventory/chilled', { params }),
  getFrozen: (params?: Record<string, string>) => api.get('/inventory/frozen', { params }),
  getByStorage: () => api.get('/inventory/by-storage'),
  getOutOfStock: (params?: Record<string, string>) => api.get('/inventory/out-of-stock', { params }),
  getExpiryAlert: (days?: number) => api.get('/inventory/expiry-alert', { params: { days } }),
  getById: (id: string) => api.get(`/inventory/${id}`),
};

// Shipments
export const shipmentsApi = {
  getChilled: (params?: Record<string, string>) => api.get('/shipments/chilled', { params }),
  getFrozen: (params?: Record<string, string>) => api.get('/shipments/frozen', { params }),
  getById: (id: string) => api.get(`/shipments/${id}`),
  create: (data: any) => api.post('/shipments', data),
  update: (id: string, data: any) => api.put(`/shipments/${id}`, data),
  delete: (id: string) => api.delete(`/shipments/${id}`),
};

// Settlements
export const settlementsApi = {
  getAll: (params?: Record<string, string>) => api.get('/settlements', { params }),
  getPending: (params?: Record<string, string>) => api.get('/settlements/pending', { params }),
  getById: (id: string) => api.get(`/settlements/${id}`),
  create: (data: any) => api.post('/settlements', data),
  confirm: (id: string, data: any) => api.put(`/settlements/${id}/confirm`, data),
  settle: (id: string) => api.put(`/settlements/${id}/settle`),
};

// Invoices
export const invoicesApi = {
  getAll: (params?: Record<string, string>) => api.get('/invoices', { params }),
  getUnbilled: (params?: Record<string, string>) => api.get('/invoices/unbilled', { params }),
  getById: (id: string) => api.get(`/invoices/${id}`),
  create: (data: any) => api.post('/invoices', data),
  confirm: (id: string, data: any) => api.put(`/invoices/${id}/confirm`, data),
  payment: (id: string, data: any) => api.put(`/invoices/${id}/payment`, data),
};

// Users
export const usersApi = {
  getAll: (params?: Record<string, string>) => api.get('/users', { params }),
  getById: (id: string) => api.get(`/users/${id}`),
  create: (data: any) => api.post('/users', data),
  update: (id: string, data: any) => api.put(`/users/${id}`, data),
  delete: (id: string) => api.delete(`/users/${id}`),
  invite: (data: any) => api.post('/users/invite', data),
  validateInvite: (token: string) => api.get(`/users/invite/${token}`),
  acceptInvite: (token: string, data: any) => api.post(`/users/invite/${token}/accept`, data),
};

// Upload
export const uploadApi = {
  upload: (file: File, folder?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (folder) formData.append('folder', folder);
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  list: (prefix?: string) => api.get('/upload', { params: { prefix } }),
  delete: (key: string) => api.delete(`/upload/${key}`),
};

// Notifications
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
};

// Auth
export const authApi = {
  login: (data: { email: string; password: string }) => api.post('/auth/login', data),
  getMe: (userId: string) => api.get('/auth/me', { headers: { 'X-User-Id': userId } }),
  updateProfile: (userId: string, data: { name: string; email: string }) => 
    api.put('/auth/profile', data, { headers: { 'X-User-Id': userId } }),
  changePassword: (userId: string, data: { currentPassword: string; newPassword: string }) => 
    api.put('/auth/password', data, { headers: { 'X-User-Id': userId } }),
  setup: (data: { email: string; name: string; password: string }) => api.post('/auth/setup', data),
};

